import React, { useEffect, useState } from 'react';
import PhoneNumber from '../../components/inputs/PhoneNumber';
import { useAppContext } from '../../contexts/AppContext';
import { AtendentesService } from '../atendentes/Service';
import FileUploadUser from '../../components/files/FileUploadUser';
import AvatarUser from '../../components/AvatarUser';
import MultiSelect from '../../components/inputs/MultiSelect';
import { UserService } from '../configuracoes/usuario/Service';
import { SettingsRow, SettingsSection } from '../configuracoes/estabelecimento/Form/SettingsGroup';
import {
    SelfSchedulingRestrictionFields,
    SelfSchedulingRestrictionValue,
} from './SelfSchedulingRestrictionFields';

interface Doc {
    uid: string;
    displayName: string;
    email: string;
    photoURL: string;
    phoneNumber: string;
    disabled: boolean;
    type: string;
    emailVerified: boolean;
    gruposIds: string[];
    atendentes?: string[];
    selfSchedulingBlocked?: boolean;
    selfSchedulingBlockMode?: 'ALL' | 'WEEK_DAYS';
    selfSchedulingBlockedWeekDays?: number[];
}

interface Update {
    displayName?: string;
    email?: string;
    phoneNumber?: string;
    photoURL?: string;
    atendentes?: string[];
    selfSchedulingBlocked?: boolean;
    selfSchedulingBlockMode?: 'ALL' | 'WEEK_DAYS';
    selfSchedulingBlockedWeekDays?: number[];
}

const service = new AtendentesService();
const userService = new UserService();

export default function ClientesForm({
                                         doc,
                                         cancel,
                                         callback,
                                     }: {
    doc?: Doc;
    callback?: (update: Update) => void;
    cancel: () => void;
}) {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(false);

    const [name, setName] = useState(doc?.displayName || '');
    const [email, setEmail] = useState(doc?.email || '');
    const [phone, setPhone] = useState(doc?.phoneNumber || '');
    const [photoURL, setPhotoURL] = useState(doc?.photoURL || '');
    const [atendentesIds, setAtendentesIds] = useState<string[]>(doc?.atendentes || []);
    const [selfScheduling, setSelfScheduling] = useState<SelfSchedulingRestrictionValue>({
        blocked: Boolean(doc?.selfSchedulingBlocked),
        mode: doc?.selfSchedulingBlockMode || 'ALL',
        weekDays: doc?.selfSchedulingBlockedWeekDays || [],
    });
    const [atendentes, setAtendentes] = useState<any[]>([]);
    const [loadingAtendentes, setLoadingAtendentes] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        setAtendentesIds(doc?.atendentes || []);
        setSelfScheduling({
            blocked: Boolean(doc?.selfSchedulingBlocked),
            mode: doc?.selfSchedulingBlockMode || 'ALL',
            weekDays: doc?.selfSchedulingBlockedWeekDays || [],
        });
    }, [
        doc?.uid,
        doc?.atendentes,
        doc?.selfSchedulingBlocked,
        doc?.selfSchedulingBlockMode,
        doc?.selfSchedulingBlockedWeekDays,
    ]);

    useEffect(() => {
        const loadAtendentes = async () => {
            try {
                setLoadingAtendentes(true);
                const result = await service.getAll();
                setAtendentes(result?.data?.data || []);
            } catch (error) {
                console.error('Erro ao carregar atendentes:', error);
            } finally {
                setLoadingAtendentes(false);
            }
        };

        loadAtendentes();
    }, []);

    useEffect(() => {
        const loadClientDetails = async () => {
            if (!doc?.uid || doc?.atendentes !== undefined) {
                return;
            }

            try {
                const { data } = await userService.getClientDetails(doc.uid);
                setAtendentesIds(data?.atendentes || []);
            } catch (error) {
                console.error('Erro ao carregar dados do cliente:', error);
            }
        };

        loadClientDetails();
    }, [doc?.uid, doc?.atendentes]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            errorSnackbar('Informe o nome do cliente.');
            return;
        }

        if (doc?.email && !email.trim()) {
            errorSnackbar('O e-mail atual não pode ser removido. Informe um novo e-mail válido.');
            return;
        }

        if (!doc?.uid && !email.trim() && !phone) {
            errorSnackbar('É necessário informar um e-mail ou telefone.');
            return;
        }

        if (selfScheduling.blocked && selfScheduling.mode === 'WEEK_DAYS' && selfScheduling.weekDays.length === 0) {
            errorSnackbar('Selecione ao menos um dia para bloquear.');
            return;
        }

        const restriction = {
            selfSchedulingBlocked: selfScheduling.blocked,
            selfSchedulingBlockMode: selfScheduling.blocked ? selfScheduling.mode : undefined,
            selfSchedulingBlockedWeekDays: selfScheduling.blocked && selfScheduling.mode === 'WEEK_DAYS'
                ? selfScheduling.weekDays
                : [],
        };

        setLoading(true);

        try {
            if (!!doc?.uid) {
                await userService.updateClient({
                    userId: doc.uid,
                    displayName: name.trim(),
                    email: email.trim(),
                    phoneNumber: phone,
                    photoURL,
                    atendentes: atendentesIds,
                    ...restriction,
                });

                successSnackbar('Cliente editado com sucesso!');
            } else {
                await userService.create({
                    name: name.trim(),
                    email: email.trim(),
                    phone,
                    atendentes: atendentesIds,
                    ...restriction,
                });

                successSnackbar('Cliente adicionado com sucesso!');
            }

            callback?.({
                displayName: name.trim(),
                email: email.trim(),
                phoneNumber: phone,
                photoURL,
                atendentes: atendentesIds,
                ...restriction,
            });
        } catch (error: any) {
            errorSnackbar(
                error?.response?.data?.message ||
                (doc?.uid ? 'Erro ao atualizar cliente.' : 'Ocorreu um erro ao adicionar o cliente.'),
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="estab-settings estab-settings--sheet space-y-6">
            {!!doc?.uid && (
                <SettingsSection title="Foto">
                    <FileUploadUser
                        value={photoURL}
                        setValue={setPhotoURL}
                        onLoadingChange={setUploadingImage}
                    >
                        {({ loading: loadingFile, fileData, error, handleChange, removeFile, inputRef }) => {
                            const previewUrl = fileData?.url;
                            const isBusy = loading || loadingFile;

                            return (
                                <div className="flex items-center gap-4 p-4">
                                    <div className="relative shrink-0">
                                        <AvatarUser
                                            name={name || 'Cliente'}
                                            photoURL={previewUrl}
                                            size="h-20 w-20 !text-2xl"
                                        />
                                        {loadingFile && (
                                            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                                                <span className="loading loading-spinner loading-sm text-white"></span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex min-w-0 flex-col items-start gap-1">
                                        <input
                                            disabled={isBusy}
                                            type="file"
                                            ref={inputRef}
                                            accept="image/*"
                                            onChange={handleChange}
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            className="estab-link-button"
                                            disabled={isBusy}
                                            onClick={() => inputRef.current?.click()}
                                        >
                                            {previewUrl ? 'Alterar foto' : 'Escolher foto'}
                                        </button>
                                        {!!previewUrl && (
                                            <button
                                                type="button"
                                                disabled={isBusy}
                                                onClick={removeFile}
                                                className="estab-link-button estab-link-button--destructive"
                                            >
                                                Remover
                                            </button>
                                        )}
                                        {!!error && <div className="text-xs text-error">{error}</div>}
                                    </div>
                                </div>
                            );
                        }}
                    </FileUploadUser>
                </SettingsSection>
            )}

            <SettingsSection
                title="Dados"
                footer={doc?.uid ? 'Nome, e-mail, telefone e foto valem para todos os estabelecimentos do cliente.' : 'Informe ao menos e-mail ou telefone.'}
            >
                <SettingsRow label="Nome" htmlFor="client-name">
                    <input
                        id="client-name"
                        type="text"
                        className="input estab-field"
                        placeholder="Nome do cliente"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={loading}
                    />
                </SettingsRow>
                <SettingsRow label="E-mail" htmlFor="client-email">
                    <input
                        id="client-email"
                        type="email"
                        className="input estab-field"
                        placeholder="cliente@email.com"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />
                </SettingsRow>
                <SettingsRow label="Telefone" htmlFor="phone">
                    <PhoneNumber
                        value={phone}
                        setValue={setPhone}
                        label="Número de telefone"
                        hideLabel
                        placeholder="+55 (00) 0 0000-0000"
                        inputClassName="input estab-field"
                    />
                </SettingsRow>
            </SettingsSection>

            <SettingsSection title="Atendentes" footer="Atendentes responsáveis por este cliente.">
                <SettingsRow stacked>
                    <MultiSelect
                        options={atendentes.map((atendente: any) => ({
                            value: atendente.uid,
                            label: atendente.displayName || atendente.email,
                        }))}
                        value={atendentesIds}
                        onChange={(selected) => setAtendentesIds(selected as string[])}
                        placeholder="Selecione atendentes"
                        loading={loadingAtendentes}
                        disabled={loading || loadingAtendentes}
                        menuOverlay
                        multiple
                    />
                </SettingsRow>
            </SettingsSection>

            <SelfSchedulingRestrictionFields
                value={selfScheduling}
                onChange={setSelfScheduling}
                disabled={loading}
            />

            <div className="estab-savebar gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    disabled={uploadingImage || loading}
                    onClick={cancel}
                >
                    Cancelar
                </button>

                <button
                    type="submit"
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={uploadingImage || loading}
                >
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    {doc?.uid ? 'Salvar alterações' : 'Adicionar cliente'}
                </button>
            </div>
        </form>
    );
}
